import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { CommunityStructure, Conversation, SearchResponse } from "@atrium/shared";
import { api, qs } from "../../lib/api";
import { useDebounced, useMyCommunities } from "../../lib/hooks";
import { queryClient } from "../../lib/queryClient";
import { useRecent, type RecentItem } from "../../lib/recent";
import { useUI } from "../../lib/uiStore";
import { Avatar, CommunityIcon } from "../../components/ui/Avatar";
import { IconHash, IconHistory, IconMessages, IconSearch, IconVolume } from "../../components/icons";
import { useMe } from "../auth/AuthProvider";
import { fuzzyFilter } from "./fuzzy";
import { KeyHint, PaletteDialog, type PaletteItem, type PaletteSection } from "./PaletteDialog";
import { focusSearchInput } from "./actions";

/*
 * Ctrl/Cmd+K: jump to any community, channel, conversation or person.
 * Empty query lists recent places. Prefixes narrow the search: # channels,
 * @ people, + communities; > switches to the command palette.
 */

type Mode = "all" | "channels" | "people" | "communities";
const PREFIX: Record<string, Mode> = { "#": "channels", "@": "people", "+": "communities" };

const LIMIT = 8;

export function QuickSwitcher({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const me = useMe();
  const navigate = useNavigate();
  const setOverlay = useUI((s) => s.setOverlay);
  const [raw, setRaw] = useState("");
  const recent = useRecent();
  const { data: communities = [] } = useMyCommunities();
  const conversations = useQuery({
    queryKey: ["conversations"],
    queryFn: () => api.get<{ items: Conversation[] }>("/api/conversations").then((r) => r.items),
    enabled: open,
  });

  const mode: Mode = PREFIX[raw[0]] ?? "all";
  const q = mode === "all" ? raw.trim() : raw.slice(1).trim();
  const dq = useDebounced(q, 200);
  const remoteUsers = useQuery({
    queryKey: ["switcher", "users", dq],
    queryFn: () => api.get<SearchResponse>(`/api/search${qs({ q: dq, type: "users", limit: 6 })}`),
    enabled: open && dq.length >= 1 && (mode === "all" || mode === "people"),
    staleTime: 30_000,
  });
  const remoteChannels = useQuery({
    queryKey: ["switcher", "channels", dq],
    queryFn: () => api.get<SearchResponse>(`/api/search${qs({ q: dq, type: "channels", limit: 8 })}`),
    enabled: open && dq.length >= 1 && (mode === "all" || mode === "channels"),
    staleTime: 30_000,
  });

  const go = (path: string) => () => navigate(path);

  const sections = useMemo<PaletteSection[]>(() => {
    const iconFor = (r: RecentItem) =>
      r.kind === "channel" ? (
        <IconHash className="icon size-[18px]" />
      ) : r.kind === "community" ? (
        <CommunityIcon src={r.iconUrl} name={r.label} size="xs" />
      ) : (
        <Avatar src={r.iconUrl} name={r.label} size="xs" />
      );
    const recentItems: PaletteItem[] = recent
      .filter((r) => mode === "all" || (mode === "channels" && r.kind === "channel") || (mode === "people" && r.kind === "profile") || (mode === "communities" && r.kind === "community"))
      .map((r) => ({ id: `r:${r.kind}:${r.id}`, label: r.label, sub: r.sub, icon: iconFor(r), hint: <IconHistory className="icon size-3.5" aria-label="Recent" />, run: go(r.path) }));

    if (!q) {
      return [
        { title: "Recent", items: recentItems.slice(0, LIMIT) },
        ...(recentItems.length < 3 && mode !== "people"
          ? [{ title: "Your communities", items: communities.slice(0, LIMIT).map((c) => ({ id: `c:${c.id}`, label: c.name, icon: <CommunityIcon src={c.iconUrl} name={c.name} size="xs" />, run: go(`/c/${c.id}`) })) }]
          : []),
      ];
    }

    const out: PaletteSection[] = [];
    // Channels: from community structures already loaded, then the server's matches.
    if (mode === "all" || mode === "channels") {
      const seen = new Set<string>();
      const local = queryClient
        .getQueriesData<CommunityStructure>({ queryKey: ["structure"] })
        .flatMap(([, s]) => (s ? s.channels.map((c) => ({ c, community: s.community })) : []));
      const items: PaletteItem[] = [];
      for (const { c, community } of fuzzyFilter(local, q, (x) => [x.c.name])) {
        seen.add(c.id);
        items.push({ id: `ch:${c.id}`, label: c.name, sub: community.name, icon: c.type === "voice" ? <IconVolume className="icon size-[18px]" /> : <IconHash className="icon size-[18px]" />, run: go(`/c/${community.id}/${c.id}`) });
      }
      for (const c of remoteChannels.data?.channels ?? []) {
        if (seen.has(c.id)) continue;
        items.push({ id: `ch:${c.id}`, label: c.name, sub: c.community.name, icon: c.type === "voice" ? <IconVolume className="icon size-[18px]" /> : <IconHash className="icon size-[18px]" />, run: go(`/c/${c.community.id}/${c.id}`) });
      }
      out.push({ title: "Channels", items: items.slice(0, mode === "channels" ? 20 : 5) });
    }
    if (mode === "all" || mode === "communities") {
      out.push({
        title: "Communities",
        items: fuzzyFilter(communities, q, (c) => [c.name, c.slug])
          .slice(0, mode === "communities" ? 20 : 5)
          .map((c) => ({ id: `c:${c.id}`, label: c.name, icon: <CommunityIcon src={c.iconUrl} name={c.name} size="xs" />, run: go(`/c/${c.id}`) })),
      });
    }
    if (mode === "all") {
      const convs = conversations.data ?? [];
      const title = (c: Conversation) => c.name || c.members.filter((u) => u.id !== me.id).map((u) => u.displayName).join(", ") || "Just you";
      out.push({
        title: "Messages",
        items: fuzzyFilter(convs, q, (c) => [title(c), ...c.members.filter((u) => u.id !== me.id).map((u) => u.username)])
          .slice(0, 5)
          .map((c) => {
            const other = c.kind === "direct" ? c.members.find((u) => u.id !== me.id) : undefined;
            return { id: `d:${c.id}`, label: title(c), icon: other ? <Avatar src={other.avatarUrl} name={other.displayName} size="xs" /> : <IconMessages className="icon size-[18px]" />, run: go(`/messages/${c.id}`) };
          }),
      });
    }
    if (mode === "all" || mode === "people") {
      out.push({
        title: "People",
        items: (remoteUsers.data?.users ?? [])
          .filter((u) => u.id !== me.id)
          .map((u) => ({ id: `u:${u.id}`, label: u.displayName, sub: `@${u.username}`, icon: <Avatar src={u.avatarUrl} name={u.displayName} size="xs" />, run: go(`/u/${u.username}`) })),
      });
    }
    // Recent matches first when they fit the query.
    const recentHits = fuzzyFilter(recentItems, q, (i) => [i.label]).slice(0, 3);
    const ids = new Set(recentHits.map((i) => i.id.replace(/^r:[a-z]+:/, "")));
    const dedup = out.map((s) => ({ ...s, items: s.items.filter((i) => !ids.has(i.id.split(":")[1])) }));
    return [{ title: "Recent", items: recentHits }, ...dedup, { title: "Search", items: [{ id: "search", label: `Search for “${q}”`, icon: <IconSearch className="icon size-[18px]" />, run: () => { navigate(`/search?q=${encodeURIComponent(q)}`); focusSearchInput(); } }] }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, mode, recent, communities, conversations.data, remoteUsers.data, remoteChannels.data, me.id]);

  return (
    <PaletteDialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setRaw("");
      }}
      label="Quick switcher"
      placeholder="Where would you like to go?"
      query={raw}
      onQueryChange={(v) => {
        if (v === ">") {
          setRaw("");
          setOverlay("palette");
          return;
        }
        setRaw(v);
      }}
      sections={sections}
      loading={remoteUsers.isFetching || remoteChannels.isFetching}
      empty={q ? `Nothing matches “${q}”` : "Visit a community, channel or profile and it will show up here."}
      footer={
        <>
          <KeyHint keys={["↑", "↓"]} label="Move" />
          <KeyHint keys={["Enter"]} label="Open" />
          <span className="ml-auto hidden sm:inline">
            <b className="font-medium text-fg-2">#</b> channels · <b className="font-medium text-fg-2">@</b> people · <b className="font-medium text-fg-2">+</b> communities · <b className="font-medium text-fg-2">&gt;</b> commands
          </span>
        </>
      }
    />
  );
}
